/**
 * A pulley wheel.
 *
 * Five ports, and which one is used is what a join MEANS: the axle is where
 * it is bolted up or a rope is tied off, and the four rims are where a rope
 * runs over it. Which side a rope lies on is the student's choice; which way
 * it WRAPS is not — that is read from the geometry, because that one is
 * physics.
 *
 * TWO rims, left and right: the ends of the piece of rope the bench draws
 * wrapped over the top of a free wheel. Which way a rope actually wraps is
 * read from the geometry, so a rope arriving level still finds its tangent;
 * the rim only says which side it is on. (There were top and bottom rims too,
 * for a level rope; they went when the rims became that rope piece's ends.)
 */
import { JOINS, numbered, PartKind } from '../registry';
import { ACROSS, DOWN } from '../vec';
import { Part, Port, RopeContact, Vec2, World } from '../types';

/**
 * The wheel a bench is scaled by: 20 cm of radius, 40 of diameter. It goes
 * from 0.1 to 0.3.
 *
 * Exported because it is the bench's unit of size and not just this file's
 * default — a weight is sized against it, so the two cannot be set apart.
 */
export const SHEAVE_R = 0.2;
/**
 * A pulley fastened ON a table or a wedge: 60% of an ordinary one — 24 cm
 * across rather than 40. (It was 40%, 16 cm; the blocks it sets the height of
 * read as slivers on a slope, and were drawn half again as tall.)
 *
 * At full size a wheel at a wedge's apex is a boulder on its summit, and it
 * dwarfs the blocks either side of it. It is also the height every block
 * standing on a face is given (`WEIGHT_ON_FACE_HALF`), so that a rope from a
 * block to it runs parallel to the face; the two are one number for that
 * reason and change together.
 */
export const SHEAVE_R_ON_FACE = SHEAVE_R * 0.6;
export const SHEAVE_R_MIN = 0.1;

/** Where a rope runs on this wheel: its groove, or the rim if it has none. */
export const wrapOf = (d: SheaveData): number => d.wrap ?? d.radius;
export const SHEAVE_R_MAX = 0.3;

export interface SheaveData {
    /** The DISC: how big the wheel is drawn, clicked and bumped into. */
    radius: number;
    /**
     * The GROOVE: the radius the rope runs at, where it touches and wraps.
     * Absent, the rope runs at the rim. Smaller than the disc is a wheel with
     * a deep groove — how a pulley fastened to a wedge keeps its size and still
     * leads a rope parallel to the face (`SHEAVE_R_ON_FACE`).
     */
    wrap?: number;
    /** kg. Zero is the ideal sheave every textbook starts with. */
    mass: number;
    /**
     * A BECKET: an eye on the block's shell, this far along x from the axle.
     *
     * A bare wheel has nowhere to tie a rope's dead end except its axle, and
     * the axle is the block's centre line — so the run leaving it is the mean
     * of the runs wrapping the wheel, which means it leaves at a slant, and a
     * slanted run does not carry its whole tension upward. A tackle built that
     * way stops being the ratio it claims: measured on the 3-segment preset,
     * the tension went 39.2 N to 52.6 N.
     *
     * A real block is wider than its wheel for exactly this reason. The becket
     * sits out beside the sheave, on the next run's own line, so the dead end
     * rises vertically like every other part of the fall. Signed: negative is
     * to the left.
     *
     * It changes no physics beyond where that one force acts — the shell is
     * rigid, and this bench does not turn bodies — but where a force acts is
     * the whole of what was wrong.
     */
    becket?: number;
}


export const sheave: PartKind<SheaveData> = {
    key: 'sheave',
    label: 'Pulley',
    home: 1.0,
    create: (at, id) => ({ id, kind: 'sheave', at, data: { radius: SHEAVE_R, mass: 0 } }),
    ports: (part): Port[] => {
        // The rims are where the ROPE is, so the groove, not the disc.
        const r = wrapOf(part.data);
        const b = part.data.becket;
        return [
            { id: 'rimL', partId: part.id, role: 'guide', joins: JOINS.oneRope, at: { x: part.at.x - r, y: part.at.y } },
            { id: 'axle', partId: part.id, role: 'anchorage', joins: JOINS.hub, at: { ...part.at } },
            { id: 'rimR', partId: part.id, role: 'guide', joins: JOINS.oneRope, at: { x: part.at.x + r, y: part.at.y } },
            ...(b === undefined ? [] : [{
                id: 'becket', partId: part.id, role: 'anchorage' as const, joins: JOINS.oneLine,
                at: { x: part.at.x + b, y: part.at.y },
            }]),
        ];
    },
    mass: (part) => part.data.mass,
    inertia: (part) => 0.5 * part.data.mass * part.data.radius ** 2,
    /* A block with a becket is as wide as its shell, not as its wheel — the
       eye has to be inside the part a reader can click and drag. */
    extent: (part) => ({
        x: Math.max(part.data.radius, Math.abs(part.data.becket ?? 0) + part.data.radius * 0.4),
        y: part.data.radius,
    }),
    /* Two freedoms — a wheel moves in a plane. See `weight.freedom`. */
    freedom: () => ({ axes: [ACROSS, DOWN] }),
    /**
     * An ideal sheave cannot hold a tension difference, so a rope over it is
     * one span and carries one tension.
     *
     * FALSE even when it has mass, and that is deliberate rather than an
     * oversight: a wheel only holds a difference through the torque that spins
     * it up, and its rotation is not a coordinate yet (phase 8). Returning
     * true before then would divide the rope into two spans with nothing
     * coupling them — two rows for one degree of freedom, and the bench would
     * report the whole arrangement as having no answer. When `spins` is real,
     * this becomes `part.data.mass > 0`.
     */
    gripsRope: () => false,
    bondable: true,
    keyFacts: (part) => [`r ${Math.round(part.data.radius * 100)} cm`, ...(part.data.mass > 0 ? [`${+part.data.mass.toFixed(2)} kg`] : [])],
    /** Fastened to a table or a wedge the rope runs in a SMALL groove — at the
     *  height of a block standing there, so a rope from one runs parallel to
     *  the face — and it sits flush on the point. The disc keeps its size. */
    onFace: () => ({ data: { wrap: SHEAVE_R_ON_FACE }, flush: true }),
    /**
     * Where the rope touches: the TANGENT point, which only the RUN knows.
     *
     * So this does not answer it, and cannot. A tangent point depends on where
     * the other end of the run is, which is a fact about the arrangement and
     * not about the wheel — `segmentsOf` solves it, and `at` here is only the
     * fallback for a run that has no tangent at all (a part inside the wheel).
     * What this does say is `wrapRadius`, which is the wheel's whole
     * contribution: the radius the rope is tangent to and wraps at.
     *
     * It was a POINT for one round, deliberately. Meeting the rope at a fixed
     * rim — `x ± r` — is only right while the run is vertical, and at any
     * other angle it was a guess that happened to look right; rather than keep
     * a guess, the radius was taken out of the arithmetic entirely so that
     * every force had one unambiguous line of action while the solver was
     * being rebuilt. Tangency is the version that is true at every angle, and
     * it is what makes the drawing and the physics the same object again.
     */
    ropeContact: (part, port): RopeContact => (port.role === 'guide'
        /* Over the RIM: the run is tangent, and `segmentsOf` solves where. All
         * this says is the radius it is tangent to. */
        ? { at: { ...part.at }, wrapRadius: wrapOf(part.data) }
        /* Tied off at the AXLE: the rope ends here, it does not run over
         * anything, and where it meets the wheel is the axle — the centre. A
         * tangent belongs to a rope going ROUND, and applying one to a rope
         * hung from the axle pushed it out to the rim, so a pulley slung from
         * a hook appeared to hang off its own edge. */
        : { at: { ...port.at } }),
    tag: 'P',
    /** Left rim, right rim, centre. Which one a rope is on is the student's
     *  choice and it never changes; this is how they can see that. */
    portName: (port) => ({ rimL: 'L', rimR: 'R', axle: 'C', becket: 'B' } as Record<string, string>)[port.id] ?? null,
    /** Beside the rim, clear of both.
     *
     *  It used to sit ON the wheel, which was the one place nothing else was
     *  ever drawn — but a wheel is 16 cm across now and a name does not fit
     *  inside one. Out to the right instead: the ropes that leave the rims run
     *  vertically through `x ± r`, so a label starting past that is clear of
     *  them however the rope is threaded. */
    /**
     * INSIDE the wheel, just above the axle.
     *
     * Out to the side it was a label with a gap between it and the thing it
     * named, and on a bench where two wheels sit close it landed between them
     * and belonged to neither. A wheel is the one part here with a broad face
     * to write on, so the name goes on it — above the centre, where the axle
     * dot is not, and where a rope wrapping the rim never reaches.
     */
    nameAt: (part) => ({
        at: { x: part.at.x, y: part.at.y + part.data.radius * 0.42 },
        anchor: 'middle' as const,
    }),
    name: (part, w) => numbered(part, w, 'P'),
};
